import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import "@/App.css";

// Lazy load all pages for better initial load
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const DebtsPage = lazy(() => import("@/pages/debts"));
const CustomerChecksPage = lazy(() => import("@/pages/CustomerChecksPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const TransactionsPage = lazy(() => import("@/pages/TransactionsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const ActivityLogPage = lazy(() => import("@/pages/ActivityLogPage"));
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));

// Create a client with optimized caching config
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // Data stays fresh for 60 seconds
      cacheTime: 10 * 60 * 1000, // Cache for 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-100 flex flex-col items-center justify-center gap-6">
    <div className="flex items-center gap-3">
      <img src="/logo.png" alt="PayFlow" className="w-12 h-12 rounded-xl" />
      <span className="text-2xl font-bold text-gray-800">PayFlow</span>
    </div>
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-500 text-base">Yükleniyor...</p>
    </div>
  </div>
);

// Lightweight page loader for lazy components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400 text-sm">Sayfa yükleniyor...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
              <Route path="borclar" element={<Suspense fallback={<PageLoader />}><DebtsPage /></Suspense>} />
              <Route path="musteri-cekleri" element={<Suspense fallback={<PageLoader />}><CustomerChecksPage /></Suspense>} />
              <Route path="giderler" element={<Suspense fallback={<PageLoader />}><ExpensesPage /></Suspense>} />
              <Route path="islemler" element={<Suspense fallback={<PageLoader />}><TransactionsPage /></Suspense>} />
              <Route path="takvim" element={<Suspense fallback={<PageLoader />}><CalendarPage /></Suspense>} />
              <Route path="raporlar" element={<Suspense fallback={<PageLoader />}><ReportsPage /></Suspense>} />
              <Route path="pazaryeri" element={<Suspense fallback={<PageLoader />}><MarketplacePage /></Suspense>} />
              <Route path="ayarlar" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
              <Route path="islem-gecmisi" element={<Suspense fallback={<PageLoader />}><ActivityLogPage /></Suspense>} />
            </Route>
          </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
